/**
 * Canonical league team names used by standings and admin assignment tools.
 */
export const LEAGUE_TEAM_NAMES = [
  'Team AC Slater',
  'Team Bruddog',
  'Team DPS',
  'Team Eifer',
  'Team Nos',
  'Team Nostradamus',
  'Team ptitteri',
  'Team Regulator',
  'Team MX',
  'Team Toolie',
  'Team VikingMoe',
] as const

/**
 * Allowed team name values for frontend admin assignment state.
 */
export type LeagueTeamName = (typeof LEAGUE_TEAM_NAMES)[number]

/**
 * Public branding used for each persisted league team assignment.
 */
export interface LeagueTeamBranding {
  displayName: string
  captainName: string
  imageSrc: string
}

/**
 * Display metadata keyed by the stored team assignment names.
 */
export const LEAGUE_TEAM_BRANDING = {
  'Team Bruddog': {
    displayName: "Bru's Your Daddy",
    captainName: 'Bruddog',
    imageSrc: '/team-images/bruddog.png',
  },
  'Team DPS': {
    displayName: 'Pick Six Pack Swoldiers',
    captainName: 'DPS',
    imageSrc: '/team-images/dps.png',
  },
  'Team Eifer': {
    displayName: 'Red Hot Chilli Tacos',
    captainName: 'Eifer',
    imageSrc: '/team-images/eifer.png',
  },
  'Team MX': {
    displayName: 'The Bowling Stones',
    captainName: 'MX',
    imageSrc: '/team-images/mx.png',
  },
  'Team Nos': {
    displayName: 'Full Time Tappers',
    captainName: 'Nos',
    imageSrc: '/team-images/nos.png',
  },
  'Team Nostradamus': {
    displayName: 'Teenage Mutant Nostra Dongles',
    captainName: 'Nostradamus',
    imageSrc: '/team-images/nostradamus.png',
  },
  'Team ptitteri': {
    displayName: 'Cocktails & Dreams',
    captainName: 'ptitteri',
    imageSrc: '/team-images/ptitteri.png',
  },
  'Team Regulator': {
    displayName: 'Regs and the Pussy Patrol',
    captainName: 'regulator',
    imageSrc: '/team-images/regulator.png',
  },
  'Team AC Slater': {
    displayName: 'Tecmo Insiders',
    captainName: 'ACSlater',
    imageSrc: '/team-images/acslater.png',
  },
  'Team Toolie': {
    displayName: 'The Infinitool Gauntlet',
    captainName: 'Toolie',
    imageSrc: '/team-images/toolie.png',
  },
  'Team VikingMoe': {
    displayName: 'Fellowship of the Tecmo',
    captainName: 'VikingMoe',
    imageSrc: '/team-images/vikingmoe.png',
  },
} satisfies Record<LeagueTeamName, LeagueTeamBranding>

/**
 * Return the public display name for a stored league team name.
 */
export function getLeagueTeamDisplayName(teamName: string | null | undefined): string {
  return teamName && teamName in LEAGUE_TEAM_BRANDING
    ? LEAGUE_TEAM_BRANDING[teamName as LeagueTeamName].displayName
    : teamName ?? 'Unassigned'
}

/**
 * Return the captain label for a stored league team name.
 */
export function getLeagueTeamCaptainName(teamName: string | null | undefined): string {
  return teamName && teamName in LEAGUE_TEAM_BRANDING
    ? LEAGUE_TEAM_BRANDING[teamName as LeagueTeamName].captainName
    : (teamName ?? 'Unassigned').replace(/^Team\s+/i, '')
}

/**
 * Return the team image URL for a stored league team name.
 */
export function getLeagueTeamImageSrc(teamName: string | null | undefined): string {
  return teamName && teamName in LEAGUE_TEAM_BRANDING
    ? LEAGUE_TEAM_BRANDING[teamName as LeagueTeamName].imageSrc
    : '/team-coming-soon.svg'
}
